import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useTranslation } from "react-i18next";
import { TextInput } from "../../../../components/TextInput";
import {
  InfoBanner,
  InfoBannerText,
  SummarizedViewContainer,
  AppointmentBlock,
  AppointmentHeadingRow,
  AppointmentHeadingText,
  TimeframeBadge,
  BulletItem,
  BulletDot,
  DescriptionLine,
  IntroParagraph,
  WarningBox,
  WarningText,
  SectionLabel,
  EditActions,
} from "./SummarizedView.styled";
import { colors } from "../../../../theme/colors";

export interface SummarizedViewHandle {
  startEdit: () => void;
}

interface SummarizedViewProps {
  content: string;
  onEditingChange?: (isEditing: boolean) => void;
  onSave: (content: string) => Promise<void>;
}

interface ParsedLine {
  type: "intro" | "appointment" | "bullet" | "description" | "cost" | "empty";
  text: string;
  appointmentLabel?: string;
  timeframe?: string;
}

function parseContent(raw: string): ParsedLine[] {
  const rawLines = raw.split("\n");
  const result: ParsedLine[] = [];
  let seenAppointment = false;

  for (const line of rawLines) {
    const trimmed = line.trim();

    if (trimmed === "") {
      result.push({ type: "empty", text: "" });
      continue;
    }

    const appointmentMatch = trimmed.match(
      /^(Appointment\s+\d+)\s*(?:[\u2014-]\s*(.+))?$/i,
    );
    if (appointmentMatch) {
      seenAppointment = true;
      result.push({
        type: "appointment",
        text: trimmed,
        appointmentLabel: appointmentMatch[1],
        timeframe: appointmentMatch[2]?.trim() || undefined,
      });
      continue;
    }

    if (trimmed.startsWith("\u2022") || trimmed.startsWith("-")) {
      result.push({
        type: "bullet",
        text: trimmed.replace(/^[\u2022-]\s*/, ""),
      });
      continue;
    }

    if (/^total care cost:/i.test(trimmed)) {
      result.push({ type: "cost", text: trimmed });
      continue;
    }

    if (!seenAppointment) {
      result.push({ type: "intro", text: trimmed });
    } else {
      result.push({ type: "description", text: trimmed });
    }
  }

  return result;
}

const SummarizedView = forwardRef<SummarizedViewHandle, SummarizedViewProps>(
  ({ content, onEditingChange, onSave }, ref) => {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(content);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
      if (!isEditing) {
        setEditValue(content);
      }
    }, [content, isEditing]);

    useEffect(() => {
      return () => {
        onEditingChange?.(false);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStartEdit = useCallback(() => {
      setEditValue(content);
      setSaveError(null);
      setIsEditing(true);
      onEditingChange?.(true);
    }, [content, onEditingChange]);

    useImperativeHandle(ref, () => ({ startEdit: handleStartEdit }), [
      handleStartEdit,
    ]);

    const handleCancel = useCallback(() => {
      setEditValue(content);
      setSaveError(null);
      setIsEditing(false);
      onEditingChange?.(false);
    }, [content, onEditingChange]);

    const handleSave = useCallback(async () => {
      setSaveError(null);
      setIsSaving(true);
      try {
        await onSave(editValue);
        setIsEditing(false);
        onEditingChange?.(false);
      } catch {
        setSaveError(t("treatmentPlan.summarizedViewSaveFailed"));
      } finally {
        setIsSaving(false);
      }
    }, [editValue, onSave, onEditingChange, t]);

    if (isEditing) {
      return (
        <>
          <WarningBox>
            <WarningAmberRoundedIcon
              sx={{ color: colors.amber.main, flexShrink: 0, mt: "1px" }}
            />
            <WarningText>
              {t("treatmentPlan.summarizedViewWarning")}
            </WarningText>
          </WarningBox>

          <SectionLabel>
            {t("treatmentPlan.summarizedViewEditLabel")}
          </SectionLabel>

          <TextInput
            multiline
            rows={Math.max(12, editValue.split("\n").length + 3)}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            error={!!saveError}
            helperText={saveError || undefined}
          />

          <EditActions>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t("treatmentPlan.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              sx={{
                backgroundColor: colors.primary.main,
                "&:hover": { backgroundColor: colors.primary.dark },
              }}
            >
              {isSaving ? t("common.saving") : t("treatmentPlan.saveChanges")}
            </Button>
          </EditActions>
        </>
      );
    }

    const parsed = parseContent(content);

    const nodes: React.ReactNode[] = [];
    let introBuffer: string[] = [];
    let currentApptBlock: {
      key: string;
      label: string;
      timeframe?: string;
      children: React.ReactNode[];
    } | null = null;

    const flushApptBlock = () => {
      if (currentApptBlock) {
        nodes.push(
          <AppointmentBlock key={currentApptBlock.key}>
            <AppointmentHeadingRow>
              <AppointmentHeadingText>
                {currentApptBlock.label}
              </AppointmentHeadingText>
              {currentApptBlock.timeframe && (
                <TimeframeBadge>{currentApptBlock.timeframe}</TimeframeBadge>
              )}
            </AppointmentHeadingRow>
            {currentApptBlock.children}
          </AppointmentBlock>,
        );
        currentApptBlock = null;
      }
    };

    const flushIntro = () => {
      if (introBuffer.length > 0) {
        nodes.push(
          <IntroParagraph key={`intro-${nodes.length}`}>
            {introBuffer.join(" ")}
          </IntroParagraph>,
        );
        introBuffer = [];
      }
    };

    for (let i = 0; i < parsed.length; i++) {
      const line = parsed[i];

      if (line.type === "intro") {
        introBuffer.push(line.text);
        continue;
      }

      flushIntro();

      if (line.type === "empty") continue;

      if (line.type === "appointment") {
        flushApptBlock();
        currentApptBlock = {
          key: `appt-${i}`,
          label: line.appointmentLabel || line.text,
          timeframe: line.timeframe,
          children: [],
        };
        continue;
      }

      if (line.type === "bullet" && currentApptBlock) {
        currentApptBlock.children.push(
          <BulletItem key={`bullet-${i}`}>
            <BulletDot>•</BulletDot>
            {line.text}
          </BulletItem>,
        );
        continue;
      }

      if (line.type === "description" && currentApptBlock) {
        currentApptBlock.children.push(
          <DescriptionLine key={`desc-${i}`}>{line.text}</DescriptionLine>,
        );
        continue;
      }
    }

    flushIntro();
    flushApptBlock();

    return (
      <>
        <InfoBanner>
          <InfoOutlinedIcon
            sx={{
              color: colors.primary.main,
              flexShrink: 0,
              mt: "1px",
              fontSize: "18px",
            }}
          />
          <InfoBannerText>
            {t("treatmentPlan.summarizedViewInfoMessage")}
          </InfoBannerText>
        </InfoBanner>
        <SummarizedViewContainer>{nodes}</SummarizedViewContainer>
      </>
    );
  },
);

SummarizedView.displayName = "SummarizedView";

export default SummarizedView;
